import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  MessageCircle, 
  ChevronRight,
  Menu,
  X,
  Instagram,
  ArrowLeft,
  MapPin,
  Calendar,
  Clock,
  Users,
  Check,
  Star,
  Utensils,
  Trophy,
  Shirt,
  Euro,
  Loader2,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SEO from "@/components/SEO";
import { EventSchema, BreadcrumbSchema } from "@/components/SchemaMarkup";
import CampRegistrationForm from "@/components/CampRegistrationForm";
import CampRegistrationSuccess from "@/components/CampRegistrationSuccess";

const API = process.env.REACT_APP_BACKEND_URL || "";

// WhatsApp Link Generator
const getWhatsAppLink = (message) => {
  const phone = "491741944996";
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${phone}?text=${encodedMessage}`;
};

// Camp Status Badge Component
const CampStatusBadge = ({ status, label, className = "" }) => {
  const statusStyles = {
    available: "bg-green-500/20 text-green-400 border-green-500/30",
    few_spots: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    sold_out: "bg-red-500/20 text-red-400 border-red-500/30",
    deadline_passed: "bg-gray-500/20 text-gray-400 border-gray-500/30"
  };
  
  return (
    <Badge className={`${statusStyles[status] || statusStyles.available} border text-xs font-semibold px-3 py-1 ${className}`}>
      {label}
    </Badge>
  );
};

// Early Bird Badge Component
const EarlyBirdBadge = ({ remaining, total }) => {
  if (remaining <= 0) return null;
  
  return (
    <div className="inline-flex items-center gap-2 bg-[#22c55e]/10 border border-[#22c55e]/30 px-3 py-1.5 rounded-full">
      <Star className="w-4 h-4 text-[#22c55e]" />
      <span className="text-[#22c55e] text-xs font-semibold">
        Frühbucher: Noch {remaining} von {total} Plätzen mit 10% Rabatt
      </span>
    </div>
  );
};

// TikTok Icon
const TikTokIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Navigation Component
const CampsNavigation = ({ camps, onOpenRegistration }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Check if any camp is available for registration
  const hasAvailableCamp = camps.some(c => c.can_register);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-md border-b border-white/5 ${isScrolled ? "py-3" : "py-4"} transition-all duration-300`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_coach-reserve-2/artifacts/rhhlwg7u_Simple%20Sports%20Football%20%26%20Soccer%20Badge%20Logo%20%282%29.png" 
                alt="Fußball Camps 2026"
                className="h-12 w-12 object-contain"
              />
              <div className="hidden sm:block">
                <p className="font-['Bebas_Neue'] text-lg text-white leading-tight">HERBSTCAMPS</p>
                <p className="text-[10px] text-[#c1ff72] tracking-[0.2em]">2026</p>
              </div>
            </Link>
          </div>

          <div className="hidden lg:flex items-center gap-6">
            <a href="#camps" className="text-[#A1A1AA] hover:text-white text-sm transition-colors">Camps</a>
            <a href="#details" className="text-[#A1A1AA] hover:text-white text-sm transition-colors">Details</a>
            <a href="#anmeldung" className="text-[#A1A1AA] hover:text-white text-sm transition-colors">Anmeldung</a>
            <Link to="/" className="text-[#A1A1AA] hover:text-white text-sm transition-colors flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" />
              Hauptseite
            </Link>
            {hasAvailableCamp ? (
              <Button
                onClick={() => onOpenRegistration()}
                className="bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#0A0A0A] font-semibold px-6 py-5 rounded-none"
                data-testid="nav-camp-registration-btn"
              >
                JETZT ANMELDEN
              </Button>
            ) : (
              <Button
                disabled
                className="bg-gray-600 text-gray-400 font-semibold px-6 py-5 rounded-none cursor-not-allowed"
                data-testid="nav-camp-registration-btn"
              >
                ALLE CAMPS AUSGEBUCHT
              </Button>
            )}
          </div>

          <button className="lg:hidden text-white p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-white/10 pt-4 space-y-3">
            <a href="#camps" className="block text-[#A1A1AA] hover:text-white py-2" onClick={() => setIsMobileMenuOpen(false)}>Camps</a>
            <a href="#details" className="block text-[#A1A1AA] hover:text-white py-2" onClick={() => setIsMobileMenuOpen(false)}>Details</a>
            <a href="#anmeldung" className="block text-[#A1A1AA] hover:text-white py-2" onClick={() => setIsMobileMenuOpen(false)}>Anmeldung</a>
            <Link to="/" className="block text-[#A1A1AA] hover:text-white py-2" onClick={() => setIsMobileMenuOpen(false)}>← Hauptseite</Link>
            {hasAvailableCamp ? (
              <Button
                onClick={() => { setIsMobileMenuOpen(false); onOpenRegistration(); }}
                className="w-full bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#0A0A0A] font-semibold py-4 rounded-none mt-4"
                data-testid="nav-mobile-camp-registration-btn"
              >
                JETZT ANMELDEN
              </Button>
            ) : (
              <Button
                disabled
                className="w-full bg-gray-600 text-gray-400 font-semibold py-4 rounded-none mt-4 cursor-not-allowed"
                data-testid="nav-mobile-camp-registration-btn"
              >
                ALLE CAMPS AUSGEBUCHT
              </Button>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = ({ camps, onOpenRegistration }) => {
  // Filter to only current (non-past) camps
  const currentCamps = camps.filter(c => !c.is_past);
  
  // Check if any camp is available
  const hasAvailableCamp = currentCamps.some(c => c.can_register);
  const allSoldOut = currentCamps.length > 0 && currentCamps.every(c => !c.can_register);
  
  // Check for early bird availability
  const hasEarlyBird = currentCamps.some(c => c.has_early_bird && c.early_bird_remaining > 0);
  
  // Find camps with few spots for urgent messaging
  const campWithFewSpots = currentCamps.find(c => c.status === "few_spots");

  return (
    <section className="relative min-h-[60vh] flex items-center justify-center pt-20 pb-16 overflow-hidden">
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=1920&q=80')` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/80 via-[#0A0A0A]/60 to-[#0A0A0A]" />
      
      <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
        {/* Status Banner */}
        {allSoldOut ? (
          <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/30 px-4 py-2 mb-8">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <span className="text-red-400 text-sm font-medium">Alle Camps sind bereits ausgebucht</span>
          </div>
        ) : hasEarlyBird ? (
          <div className="inline-flex items-center gap-2 bg-[#22c55e]/10 border border-[#22c55e]/30 px-4 py-2 mb-8">
            <Star className="w-4 h-4 text-[#22c55e]" />
            <span className="text-[#22c55e] text-sm font-medium">🎉 10% Frühbucherrabatt für die ersten 5 Anmeldungen!</span>
          </div>
        ) : campWithFewSpots ? (
          <div className="inline-flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 px-4 py-2 mb-8">
            <AlertCircle className="w-4 h-4 text-yellow-400" />
            <span className="text-yellow-400 text-sm font-medium">{campWithFewSpots.label} – Jetzt schnell anmelden!</span>
          </div>
        ) : hasAvailableCamp ? (
          <div className="inline-flex items-center gap-2 bg-[#c1ff72]/10 border border-[#c1ff72]/30 px-4 py-2 mb-8">
            <span className="w-2 h-2 bg-[#c1ff72] rounded-full animate-pulse" />
            <span className="text-[#c1ff72] text-sm font-medium">Herbstferien 2026 · Anmeldung offen</span>
          </div>
        ) : null}

        <h1 className="font-['Bebas_Neue'] text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-white mb-6 leading-[0.9]">
          {allSoldOut ? (
            <>
              HERBSTCAMPS 2026<br />
              <span className="text-red-400">AUSGEBUCHT</span>
            </>
          ) : (
            <>
              HERBSTCAMPS<br />
              <span className="text-[#c1ff72]">2026</span>
            </>
          )}
        </h1>

        {allSoldOut ? (
          <div className="max-w-3xl mx-auto mb-10">
            <p className="text-xl md:text-2xl text-white/80 mb-4">
              Alle Camps sind bereits vollständig ausgebucht.
            </p>
            <p className="text-lg text-white/60">
              Melde dich für unseren Newsletter an, um über zukünftige Camps informiert zu werden.
            </p>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto mb-10">
            <p className="text-xl md:text-2xl text-white/80 mb-4">
              4 Tage intensives Fußballtraining in den Herbstferien
            </p>
            <p className="text-lg text-white/60">
              Meinerzhagen (19.-22.10.) und Drolshagen (26.-29.10.)
            </p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {hasAvailableCamp ? (
            <Button
              onClick={() => onOpenRegistration()}
              className="bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#0A0A0A] font-bold px-10 py-7 rounded-none text-lg"
              data-testid="hero-camp-registration-btn"
            >
              Jetzt anmelden
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          ) : (
            <Button
              disabled
              className="bg-gray-600 text-gray-400 font-bold px-10 py-7 rounded-none text-lg cursor-not-allowed"
              data-testid="hero-camp-registration-btn"
            >
              Camps nicht verfügbar
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};

// Camps Section with Dynamic Status
const CampsSection = ({ camps, pastCamps, onOpenRegistration, isLoading }) => {
  // Get poster URLs from camp data or use fallbacks
  const getCampImage = (camp) => {
    if (camp.poster_url) return camp.poster_url;
    const fallbackImages = {
      "summer-camp-july-2026": "https://customer-assets.emergentagent.com/job_web-enhance-48/artifacts/p56on59v_Schwarz%20Wei%C3%9F%20Gru%CC%88n%20Dramatisch%20Beleuchtung%20Fotografie%20Fussball%20Camp%20Flyer%20A4%20%28Instagram-Post%20%2845%29%29%20%288%29%202.PNG",
      "summer-camp-august-2026": "https://customer-assets.emergentagent.com/job_web-enhance-48/artifacts/gvgrf9xp_Schwarz%20Wei%C3%9F%20Gru%CC%88n%20Dramatisch%20Beleuchtung%20Fotografie%20Fussball%20Camp%20Flyer%20A4%20%28Instagram-Post%20%2845%29%29%20%2810%29%202.PNG"
    };
    return fallbackImages[camp.id] || "";
  };

  // Filter to only current camps (non-past)
  const currentCamps = camps.filter(c => !c.is_past);

  return (
    <section id="camps" className="py-20 lg:py-32 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-[#c1ff72] text-sm uppercase tracking-wider mb-4">Herbstferien 2026</p>
          <h2 className="font-['Bebas_Neue'] text-4xl md:text-6xl text-white mb-6">
            WÄHLE DEIN <span className="text-[#c1ff72]">CAMP</span>
          </h2>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 text-[#c1ff72] animate-spin" />
          </div>
        ) : (
          <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {currentCamps.map((camp) => (
              <div 
                key={camp.id} 
                id={`camp-${camp.id}`}
                className={`bg-[#1A1A1A] border border-white/5 overflow-hidden group relative ${
                  !camp.can_register ? "opacity-90" : ""
                }`}
              >
                {/* Full Image Display with Diagonal Banner for Sold Out */}
                <div className="relative overflow-hidden">
                  <img 
                    src={getCampImage(camp)} 
                    alt={camp.name} 
                    className={`w-full h-auto object-contain ${!camp.can_register ? "grayscale-[20%]" : ""}`}
                  />
                  
                  {/* Diagonal Banner for Sold Out Camps */}
                  {camp.status === "sold_out" && (
                    <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                      <div 
                        className="absolute top-[45px] left-[-55px] w-[220px] bg-gradient-to-r from-[#8B0000] via-[#A52A2A] to-[#8B0000] text-white font-bold text-center py-2.5 shadow-lg transform -rotate-45"
                        style={{ boxShadow: '0 4px 15px rgba(139, 0, 0, 0.5)' }}
                      >
                        <span className="font-['Bebas_Neue'] text-lg tracking-wider drop-shadow-md">AUSGEBUCHT</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Deadline Passed Banner */}
                  {camp.status === "deadline_passed" && (
                    <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                      <div className="absolute top-[45px] left-[-70px] w-[250px] bg-gradient-to-r from-gray-600 via-gray-500 to-gray-600 text-white font-bold text-center py-2.5 shadow-lg transform -rotate-45">
                        <span className="font-['Bebas_Neue'] text-sm tracking-wider">ANMELDEFRIST ABGELAUFEN</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Few spots badge */}
                  {camp.status === "few_spots" && (
                    <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                      <div 
                        className="absolute top-[45px] left-[-55px] w-[220px] bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-600 text-black font-bold text-center py-2.5 shadow-lg transform -rotate-45"
                        style={{ boxShadow: '0 4px 15px rgba(234, 179, 8, 0.5)' }}
                      >
                        <span className="font-['Bebas_Neue'] text-lg tracking-wider drop-shadow-sm">WENIGE PLÄTZE</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Early Bird Badge */}
                  {camp.has_early_bird && camp.early_bird_remaining > 0 && camp.can_register && (
                    <div className="absolute top-4 right-4 bg-[#22c55e] text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg">
                      <Star className="w-3 h-3 inline mr-1" />
                      10% Rabatt - Noch {camp.early_bird_remaining} Plätze!
                    </div>
                  )}
                </div>
                
                {/* Camp Info */}
                <div className="p-6 lg:p-8">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <p className="text-[#c1ff72] text-sm font-medium">
                        {camp.location_short || (camp.name.includes("Meinerzhagen") ? "Meinerzhagen" : camp.name.includes("Drolshagen") ? "Drolshagen" : "Camp")}
                      </p>
                      <p className="font-['Bebas_Neue'] text-3xl text-white">{camp.date}</p>
                    </div>
                    <div className="text-right">
                      {camp.has_early_bird && camp.early_bird_remaining > 0 && camp.can_register ? (
                        <>
                          <p className="text-white/50 text-sm line-through">{camp.original_price || camp.price}€</p>
                          <p className="font-['Bebas_Neue'] text-4xl text-[#22c55e]">{camp.current_price}€</p>
                        </>
                      ) : (
                        <p className="font-['Bebas_Neue'] text-4xl text-[#c1ff72]">{camp.current_price || camp.price}€</p>
                      )}
                      <p className="text-white/50 text-sm">Inkl. Camp-Gear</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-[#0A0A0A] p-4 text-center">
                      <Clock className="w-5 h-5 text-[#c1ff72] mx-auto mb-2" />
                      <p className="text-white font-medium text-sm">10–14 Uhr</p>
                      <p className="text-white/50 text-xs">Täglich</p>
                    </div>
                    <div className="bg-[#0A0A0A] p-4 text-center">
                      <Users className="w-5 h-5 text-[#c1ff72] mx-auto mb-2" />
                      <p className="text-white font-medium text-sm">{camp.age_range}</p>
                      <p className="text-white/50 text-xs">Altersgruppen</p>
                    </div>
                    <div className="bg-[#0A0A0A] p-4 text-center">
                      <MapPin className="w-5 h-5 text-[#c1ff72] mx-auto mb-2" />
                      <p className="text-white font-medium text-sm">{camp.location_short || "Standort"}</p>
                      <p className="text-white/50 text-xs truncate">{camp.location?.split(",")[0]}</p>
                    </div>
                  </div>
                  
                  {/* Full location */}
                  <div className="bg-[#0A0A0A] p-3 mb-4 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#c1ff72] flex-shrink-0" />
                    <p className="text-white/70 text-sm">{camp.location}</p>
                  </div>
                  
                  {/* Includes list */}
                  {camp.includes && camp.includes.length > 0 && (
                    <div className="mb-4">
                      <p className="text-[#c1ff72] text-xs font-semibold mb-2">INKLUSIVE:</p>
                      <div className="flex flex-wrap gap-2">
                        {camp.includes.map((item, idx) => (
                          <span key={idx} className="text-xs bg-[#0A0A0A] text-white/70 px-2 py-1 rounded">✓ {item}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Registration Deadline */}
                  {camp.registration_deadline && camp.can_register && (
                    <p className="text-white/50 text-xs mb-4">Anmeldeschluss: {camp.registration_deadline}</p>
                  )}

                  {/* Status-specific messages */}
                  {camp.status === "few_spots" && (
                    <p className="text-yellow-400 text-sm mb-6 flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-4 py-3">
                      <AlertCircle className="w-4 h-4" />
                      {camp.label} – Jetzt schnell anmelden!
                    </p>
                  )}
                  
                  {camp.has_early_bird && camp.early_bird_remaining > 0 && camp.can_register && (
                    <p className="text-[#22c55e] text-sm mb-6 flex items-center gap-2 bg-[#22c55e]/10 border border-[#22c55e]/20 rounded-lg px-4 py-3">
                      <Star className="w-4 h-4" />
                      Frühbucher: Noch {camp.early_bird_remaining} von {camp.early_bird_slots} Plätzen mit 10% Rabatt!
                    </p>
                  )}

                  {camp.status === "sold_out" && (
                    <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                      <p className="text-red-400 text-sm flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Dieses Camp ist leider ausgebucht
                      </p>
                    </div>
                  )}

                  {camp.status === "deadline_passed" && (
                    <div className="mb-6 p-4 bg-gray-500/10 border border-gray-500/20 rounded-lg">
                      <p className="text-gray-400 text-sm flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Die Anmeldefrist ist abgelaufen
                      </p>
                    </div>
                  )}

                  {/* Registration Button */}
                  <div>
                    {camp.can_register ? (
                      <Button
                        onClick={() => onOpenRegistration({ id: camp.id })}
                        className="w-full bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#0A0A0A] font-semibold py-6 rounded-none"
                        data-testid={`camp-registration-btn-${camp.id}`}
                      >
                        Jetzt anmelden
                        <ChevronRight className="w-5 h-5 ml-2" />
                      </Button>
                    ) : (
                      <Button
                        disabled
                        className="w-full bg-gray-600 text-gray-400 font-semibold py-6 rounded-none cursor-not-allowed"
                        data-testid={`camp-registration-btn-${camp.id}`}
                      >
                        {camp.status === "sold_out" ? "Ausgebucht" : "Anmeldefrist abgelaufen"}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Past Camps Section */}
          {pastCamps && pastCamps.length > 0 && (
            <div className="mt-20">
              <div className="text-center mb-10">
                <p className="text-white/50 text-sm uppercase tracking-wider mb-2">Archiv</p>
                <h3 className="font-['Bebas_Neue'] text-2xl md:text-3xl text-white/70">Vergangene Camps</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-60">
                {pastCamps.map((camp) => (
                  <div key={camp.id} className="bg-[#1A1A1A]/50 border border-white/5 p-6 rounded-lg">
                    <p className="text-[#c1ff72]/50 text-xs font-medium mb-1">{camp.season === "sommer" ? "Sommercamp 2026" : "Camp"}</p>
                    <h4 className="text-white/70 font-semibold mb-2">{camp.name}</h4>
                    <p className="text-white/50 text-sm">{camp.date}</p>
                    <p className="text-white/40 text-xs mt-2">{camp.location}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </section>
  );
};

// Details Section
const DetailsSection = () => {
  const highlights = [
    { icon: Shirt, title: "Trikot-Satz", desc: "Jedes Kind erhält ein vollständiges Trikot-Set zum Behalten" },
    { icon: Utensils, title: "Verpflegung", desc: "Mittagessen, Snacks und Getränke sind inklusive" },
    { icon: Trophy, title: "Challenges", desc: "Tägliche Wettbewerbe mit tollen Preisen" },
    { icon: Users, title: "Kleine Gruppen", desc: "Altersgerechte Einteilung für optimales Training" }
  ];

  const tagesablauf = [
    { time: "10:00", activity: "Ankunft & Aufwärmen" },
    { time: "10:30", activity: "Techniktraining" },
    { time: "11:30", activity: "Pause & Snack" },
    { time: "11:45", activity: "Spielformen & Taktik" },
    { time: "12:45", activity: "Mittagessen" },
    { time: "13:15", activity: "Abschlussspiele & Challenges" },
    { time: "14:00", activity: "Ende" }
  ];

  return (
    <section id="details" className="py-20 lg:py-32 bg-[#121212]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-[#c1ff72] text-sm uppercase tracking-wider mb-4">Was dich erwartet</p>
          <h2 className="font-['Bebas_Neue'] text-4xl md:text-6xl text-white mb-6">
            VIER TAGE <span className="text-[#c1ff72]">VOLLE INTENSITÄT</span>
          </h2>
        </div>

        {/* Highlights */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {highlights.map((item) => (
            <div key={item.title} className="bg-[#1A1A1A] border border-white/5 p-6 text-center hover:border-[#c1ff72]/30 transition-all">
              <item.icon className="w-10 h-10 text-[#c1ff72] mx-auto mb-4" />
              <h3 className="font-['Bebas_Neue'] text-xl text-white mb-2">{item.title}</h3>
              <p className="text-[#A1A1AA] text-sm">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Tagesablauf */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h3 className="font-['Bebas_Neue'] text-3xl text-white mb-6">Tagesablauf</h3>
            <div className="space-y-3">
              {tagesablauf.map((item) => (
                <div key={item.time} className="flex items-center gap-4 bg-[#1A1A1A] p-4 border-l-4 border-[#c1ff72]">
                  <span className="font-['Bebas_Neue'] text-xl text-[#c1ff72] w-16">{item.time}</span>
                  <span className="text-white">{item.activity}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-[#1A1A1A] border border-white/5 p-8">
            <h3 className="font-['Bebas_Neue'] text-2xl text-white mb-6">Im Preis enthalten</h3>
            <ul className="space-y-3">
              {[
                "4 Tage professionelles Training",
                "Vollständiger Trikot-Satz",
                "Mittagessen & Snacks täglich",
                "Getränke während des Trainings",
                "Tägliche Challenges & Preise",
                "Abschluss-Turnier",
                "Urkunde & Erinnerungsfoto",
                "Qualifizierte DFB-lizenzierte Trainer"
              ].map((item) => (
                <li key={item} className="flex items-center gap-3 text-white/80">
                  <Check className="w-5 h-5 text-[#c1ff72] flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const stats = [
    { value: "180€", label: "Pro Camp" },
    { value: "4", label: "Tage Training" },
    { value: "6-13", label: "Jahre" },
    { value: "10%", label: "Frühbucher-Rabatt" }
  ];

  return (
    <section className="py-16 bg-[#c1ff72]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="font-['Bebas_Neue'] text-4xl md:text-5xl text-[#0A0A0A]">{stat.value}</p>
              <p className="text-[#0A0A0A]/70 font-medium">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = ({ camps, onOpenRegistration }) => {
  const hasAvailableCamp = camps.some(c => c.can_register);
  const allSoldOut = camps.length > 0 && camps.every(c => !c.can_register);

  return (
    <section id="anmeldung" className="py-20 lg:py-32 bg-[#0A0A0A]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-[#c1ff72] text-sm uppercase tracking-wider mb-4">Jetzt anmelden</p>
        <h2 className="font-['Bebas_Neue'] text-4xl md:text-6xl text-white mb-6">
          {allSoldOut ? (
            <>LEIDER <span className="text-red-400">AUSGEBUCHT</span></>
          ) : (
            <>SICHERE DEINEN <span className="text-[#c1ff72]">PLATZ</span></>
          )}
        </h2>
        
        {allSoldOut ? (
          <div className="mb-8">
            <p className="text-[#A1A1AA] text-lg">
              Alle Camps sind bereits vollständig ausgebucht.
            </p>
          </div>
        ) : (
          <p className="text-[#A1A1AA] text-lg mb-8 max-w-2xl mx-auto">
            Die Plätze sind begrenzt. Melde dich jetzt online an – schnell, einfach und sicher!
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
          {hasAvailableCamp ? (
            <Button
              onClick={() => onOpenRegistration()}
              className="bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#0A0A0A] font-bold px-10 py-7 rounded-none text-lg"
              data-testid="cta-camp-registration-btn"
            >
              <ChevronRight className="w-5 h-5 mr-2" />
              Jetzt online anmelden
            </Button>
          ) : (
            <Button
              disabled
              className="bg-gray-600 text-gray-400 font-bold px-10 py-7 rounded-none text-lg cursor-not-allowed"
              data-testid="cta-camp-registration-btn"
            >
              Camp ausgebucht
            </Button>
          )}
          <Button
            onClick={() => window.open(getWhatsAppLink("Hallo Yasin, ich habe eine Frage zu den Sommer-Camps 2026."), "_blank")}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10 font-bold px-10 py-7 rounded-none text-lg"
          >
            <MessageCircle className="w-5 h-5 mr-2" />
            Fragen? WhatsApp
          </Button>
        </div>

        <div className="mt-12 flex items-center justify-center gap-2 text-[#A1A1AA]">
          <MapPin className="w-5 h-5" />
          <span>Am Stadion 1 · 58540 Meinerzhagen</span>
        </div>
      </div>
    </section>
  );
};

// Footer
const CampsFooter = () => (
  <footer className="py-10 bg-[#0A0A0A] border-t border-white/5">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <img 
            src="https://customer-assets.emergentagent.com/job_coach-reserve-2/artifacts/rhhlwg7u_Simple%20Sports%20Football%20%26%20Soccer%20Badge%20Logo%20%282%29.png" 
            alt="Logo"
            className="h-10 w-10 object-contain"
          />
          <div>
            <p className="font-['Bebas_Neue'] text-white">HERBSTCAMPS 2026</p>
            <p className="text-[#71717A] text-xs">Yasin Aktas · Meinerzhagen</p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <a href="https://instagram.com/fussballtraining_aktas" target="_blank" rel="noopener noreferrer" className="text-[#A1A1AA] hover:text-[#c1ff72]">
            <Instagram className="w-5 h-5" />
          </a>
          <a href="https://tiktok.com/@fussballtraining_aktas" target="_blank" rel="noopener noreferrer" className="text-[#A1A1AA] hover:text-[#c1ff72]">
            <TikTokIcon size={20} />
          </a>
        </div>
        <div className="text-center md:text-right">
          <Link to="/" className="text-[#A1A1AA] hover:text-white text-sm">Hauptseite</Link>
          <span className="text-[#71717A] mx-2">·</span>
          <Link to="/akademie" className="text-[#c1ff72] hover:text-white text-sm">Akademie</Link>
          <p className="text-[#71717A] text-xs mt-2">© {new Date().getFullYear()} Yasin Aktas</p>
        </div>
      </div>
    </div>
  </footer>
);

// Main Page Component
export default function CampsPage() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(null);
  const [selectedCamp, setSelectedCamp] = useState(null);
  const [camps, setCamps] = useState([]);
  const [pastCamps, setPastCamps] = useState([]);
  const [isLoadingCamps, setIsLoadingCamps] = useState(true);

  // Fetch camps from backend
  useEffect(() => {
    const fetchCamps = async () => {
      try {
        const response = await axios.get(`${API}/api/camps/available`);
        setCamps(response.data.camps || []);
        setPastCamps(response.data.past_camps || []);
      } catch (error) {
        console.error("Error fetching camps:", error);
      } finally {
        setIsLoadingCamps(false);
      }
    };
    
    fetchCamps();
    setIsLoaded(true);
    window.scrollTo(0, 0);
  }, []);

  const handleOpenRegistration = (camp = null) => {
    setSelectedCamp(camp);
    setShowRegistrationForm(true);
  };

  const handleRegistrationSuccess = (data) => {
    setShowRegistrationForm(false);
    setRegistrationSuccess(data);
  };

  const handleCloseSuccess = () => {
    setRegistrationSuccess(null);
  };

  return (
    <main className={`transition-opacity duration-700 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
      <SEO 
        title="Herbstcamps 2026 | Personal Fußballtraining"
        description="Herbst Fußballcamps 2026 in Meinerzhagen und Drolshagen. 4 Tage intensives Training mit Technik, Taktik und jeder Menge Spaß. Jetzt Platz sichern!"
        path="/camps"
      />
      <BreadcrumbSchema items={[
        { name: "Startseite", url: "https://personal-fussballtraining.de/" },
        { name: "Camps", url: "https://personal-fussballtraining.de/camps" }
      ]} />
      <EventSchema 
        name="Fußball Herbstcamp '26 – Meinerzhagen"
        description="4-tägiges Fußballcamp mit professionellem Training, Technik, Spielformen und jeder Menge Spaß. Inkl. Verpflegung und Camp-Gear."
        startDate="2026-10-19"
        endDate="2026-10-22"
        location="Am Stadion 1, 58540 Meinerzhagen"
        price="180"
        image="https://customer-assets-m6fa6gv7.emergentagent.net/job_coach-reserve-2/artifacts/gygb4mrh_Mz-Herbstcamp%20-final.png"
      />
      <EventSchema 
        name="Fußball Herbstcamp '26 – Drolshagen"
        description="4-tägiges Fußballcamp mit professionellem Training, Technik, Spielformen und jeder Menge Spaß. Inkl. Verpflegung und Camp-Gear."
        startDate="2026-10-26"
        endDate="2026-10-29"
        location="Am Buscheid, 57489 Drolshagen"
        price="180"
        image="https://customer-assets-m6fa6gv7.emergentagent.net/job_coach-reserve-2/artifacts/3xb6zc0u_Drolshagen%20-%20Herbstcamp.png"
      />
      <CampsNavigation camps={camps} onOpenRegistration={handleOpenRegistration} />
      <HeroSection camps={camps} onOpenRegistration={handleOpenRegistration} />
      <CampsSection camps={camps} pastCamps={pastCamps} onOpenRegistration={handleOpenRegistration} isLoading={isLoadingCamps} />
      <StatsSection />
      <DetailsSection />
      <CTASection camps={camps} onOpenRegistration={handleOpenRegistration} />
      <CampsFooter />
      
      {/* Registration Form Modal */}
      {showRegistrationForm && (
        <CampRegistrationForm
          selectedCamp={selectedCamp}
          onClose={() => setShowRegistrationForm(false)}
          onSuccess={handleRegistrationSuccess}
        />
      )}
      
      {/* Registration Success Modal */}
      {registrationSuccess && (
        <CampRegistrationSuccess
          data={registrationSuccess}
          onClose={handleCloseSuccess}
        />
      )}
    </main>
  );
}
